import React, { memo, useCallback, useContext, useMemo, useState } from 'react';
import ConnectedFormContext from '../Context/ConnectedFormContext';
import SalesOrderContext from '../Context/SalesOrderContext';
import { useDispatch, useSelector } from 'react-redux';
import { MenuItem } from 'react-contextmenu';
import GridContainer from '../connectedForm/GridContainer';
import CommentRowContext, { CommentRowContextType } from '../Context/CommentRowContext';
import EditableDataGrid from '../EditableDataGrid';
import getIn from '../connectedForm/utils/getIn';
import { uuIdGenerator } from '../../utils/UuIdGenerator';
import { IEditableDataGridColumnDef, IEditableDataGridDropdownOptions } from '../../@types/editableDataGridTypes';
import {
  CostViewPermission,
  FilterOperation,
  IFullUserDetails,
  IProductComponent,
  ISalesOrder,
  ISalesOrderLine,
  IProductWarehouseDetailsWorkspaceQueryResult,
  IProductWarehouseDetailsWorkspaceQueryVariables,
  IProductWarehouseDetail,
  Maybe,
  ProductClass,
} from '../../graphql/schema/p4types';
import { useVarianceQuantity } from '../CellFormatters/SalesOrderStatusCellFormatter';
import { useQuery } from '@apollo/client';
import ProductWarehouseDetailsWorkspaceQuery from '../../graphql/queries/product/ProductWarehouseDetailsWorkspaceQuery.graphql';
import { IContextMenuItemProps } from '../ContextMenuItem';
import { useSingleFieldTemporarySave } from '../connectedForm/utils/handleTemporarySave';
import { usePrecision } from '../../utils/hooks';
import { Product } from '../../graphql/entities';
import DropdownEditorClean from '../DropdownEditor/DropdownEditorClean';
import { getMultiQueryKeys } from '../../graphql/entities/product/Product';
import { ComponentStatusCellFormatter } from '../CellFormatters/ComponentStatusCellFormatter';
import Formatters from '../grid-addons/formatters';

const LazyCommentRowRenderer = React.lazy(() => import('../../components/EditableDataGrid/CommentRowRenderer'));

export const ComponentContext = React.createContext(
  {} as {
    flattenedComponents: Array<IFlattenedProductComponent>;
    field: string;
    rowIndex: number;
    addCommentToRowIdx: Maybe<number>;
    setAddCommentToRowIdx: Function;
    lineOutstandingLessFreeStock: number; //ComponentStatusCellFormatter needs to know the outstanding quantity after the main line is accounted for.
  }
);

export type IFlattenedProductComponent = IProductComponent & {
  index: Array<number>;
  levelIndex: string;
  productWarehouseDetail?: IProductWarehouseDetail;
};

// flattens components into one array
const flatten = (
  productComponent: IProductComponent | IFlattenedProductComponent,
  index: Array<number>,
  deleted: boolean
) => {
  let flattened: Array<IFlattenedProductComponent> = [];
  // spread component and add index array, index identifier and cascade deleted flag
  flattened.push({
    ...productComponent,
    index,
    levelIndex: `${index.join('.')}`,
    deleted,
  });

  if (productComponent?.component?.components) {
    productComponent.component.components.forEach((component, i) => {
      // index is an array of 1-indexed numbers representing position in hierarchy e.g. [1,1,2] is 2nd child 3 levels deep
      flattened = flattened.concat(flatten(component!, [...index, i + 1], !!productComponent.deleted));
    });
  }

  return flattened;
};

type IComponentGridProps = {
  components: Array<Maybe<IProductComponent>> | undefined | null;
  field: string;
  rowIndex: number;
  performTemporarySave: () => void;
  readOnly?: boolean;
};

const useOutstandingQuantity = ({ salesOrderLine }: { salesOrderLine: ISalesOrderLine }) => {
  const localVarianceQuantity = useVarianceQuantity({ orderLine: salesOrderLine });
  return useMemo(() => {
    return (salesOrderLine.outstanding ?? 0) + localVarianceQuantity;
  }, [salesOrderLine.outstanding, localVarianceQuantity]);
};

const useComponentColumnMeta = (rowIndex?: Maybe<number>): Array<IEditableDataGridColumnDef> => {
  const updateFieldAndTemporarySave = useSingleFieldTemporarySave();
  const costPricePrecision = usePrecision('cost');
  const connectedForm = useContext(ConnectedFormContext);
  const fullUserDetails = useSelector((store: any) => store.app.fullUserDetails as Maybe<IFullUserDetails>);
  const salesOrderLine = rowIndex ? (connectedForm.values as ISalesOrder).orderLines?.[rowIndex] : undefined;

  const getSalesProductLookupData = useCallback(() => {
    return {
      customer: connectedForm?.values?.customer,
    };
  }, [connectedForm?.values?.customer]);

  const productOptions: IEditableDataGridDropdownOptions = {
    type: Product.type,
    editor: <DropdownEditorClean />,
    dropdown: {
      ...(Product.dropdown as IEditableDataGridDropdownOptions),
      clearable: false,
      selectModal: 'SalesProductLookup',
      selectModalData: getSalesProductLookupData,
      predefinedFilters: [
        {
          filterOperation: FilterOperation.Custom,
          filterParameters: {
            field: 'template',
          },
        },
      ],
      enableMulti: true,
      multiQuery: {
        ...(Product.dropdown?.multiQuery as any),
        keys: getMultiQueryKeys({
          productFilters: [
            {
              filterOperation: FilterOperation.NotEquals,
              filterParameters: {
                field: 'productClass',
                value: ProductClass.FreeType,
              },
            },
          ],
        }),
      },
      temporaryOnClick: true,
    },
  };

  //Common columns that are positioned to the left of the variable 'cost price' column definitions.
  const commonColumns: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Index',
      key: 'levelIndex',
      width: 100,
      editable: false,
      cellFormatter: ComponentStatusCellFormatter,
    },
    {
      name: 'Product',
      key: 'component',
      editable: (rowData) => !rowData.index || rowData.index.length === 1,
      editor: <DropdownEditorClean />,
      options: productOptions,
      formatter: 'dropdown',
      width: 250,
      resizable: false,
    },
    {
      name: 'Description',
      key: 'component.description',
      editable: false,
      resizable: true,
    },
    {
      name: 'Supplier',
      key: 'component.mainSupplierDetails.supplier.reference',
      editable: false,
      resizable: true,
      width: 110,
    },
  ];

  let costPriceColumns: Array<IEditableDataGridColumnDef> = [];
  if (fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.Haggle) {
    costPriceColumns = [
      {
        name: 'Cost Price',
        key: 'component.haggleCostPrice',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        width: 110,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
    ];
  } else if (fullUserDetails?.companyUser?.costViewPermission === CostViewPermission.All) {
    costPriceColumns = [
      {
        name: 'Valuation Cost Price',
        key: 'component.valuationCostPrice',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        width: 110,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
      {
        name: 'Latest Cost Price',
        key: 'component.latestCostPrice',
        formatter: Formatters.NumericFormatter,
        editable: false,
        resizable: true,
        cellClass: 'rightAlign',
        width: 110,
        options: {
          format: { decimals: costPricePrecision },
        },
      },
    ];
  }

  //Common columns that are positioned to the right of the variable 'cost price' column definitions.
  const commonColumns2: Array<IEditableDataGridColumnDef> = [
    {
      name: 'Free Stock (Total)',
      key: 'component.freeStock',
      formatter: Formatters.NumericFormatter,
      editable: false,
      resizable: true,
      cellClass: 'rightAlign',
      width: 110,
      options: {
        format: {
          decimals: (rowData) => {
            return rowData?.component?.precision;
          },
        },
      },
    },
    {
      name: 'Quantity',
      key: 'quantity',
      formatter: Formatters.NumericFormatter,
      editable: (rowData) => !rowData.index || rowData.index.length === 1,
      resizable: true,
      cellClass: 'rightAlign',
      width: 110,
      options: {
        format: {
          decimals: (rowData) => {
            return rowData?.component?.precision;
          },
        },
      },
    },
    {
      name: '',
      key: '',
      editable: true,
      resizable: true,
      formatter: Formatters.ContextOptionsFormatter,
      options: (rowData) => {
        const index = rowData.index;
        if (!index || index.length > 1) {
          return [];
        }

        return [
          {
            label: 'Delete',
            onClick: (value, close) => {
              if (close) close();
              updateFieldAndTemporarySave(`orderLines.${rowIndex}.components.${index[0] - 1}.deleted`, true);
            },
          },
        ];
      },
    },
  ];

  if (salesOrderLine?.warehouse) {
    commonColumns2.unshift({
      name: `Free Stock (${salesOrderLine?.warehouse?.description})`,
      key: 'productWarehouseDetail.freeStock',
      formatter: Formatters.NumericFormatter,
      editable: false,
      resizable: true,
      cellClass: 'rightAlign',
      width: 200,
      options: {
        format: {
          decimals: (rowData) => {
            return rowData?.component?.precision;
          },
        },
      },
    });
  }

  return [...commonColumns, ...costPriceColumns, ...commonColumns2];
};

const useProductWarehouseDetails = ({
  salesOrderLine,
  flattenedComponents,
}: {
  salesOrderLine: ISalesOrderLine;
  flattenedComponents: Array<IFlattenedProductComponent>;
}) => {
  const productIds = flattenedComponents.map((value) => value.component!.id!);
  productIds.push(salesOrderLine.product!.id!);

  const { data } = useQuery<
    IProductWarehouseDetailsWorkspaceQueryResult,
    IProductWarehouseDetailsWorkspaceQueryVariables
  >(ProductWarehouseDetailsWorkspaceQuery, {
    variables: {
      filters: [
        {
          filterOperation: FilterOperation.Equals,
          filterParameters: {
            field: 'warehouse.id',
            value: `${salesOrderLine.warehouse?.id ?? 0}`,
          },
        },
        ...productIds.map((productId) => ({
          filterOperation: FilterOperation.In,
          filterParameters: {
            field: 'product.id',
            value: `${productId}`,
          },
        })),
      ],
    },
    skip: productIds.length === 0 || !salesOrderLine.warehouse?.id,
  });

  return useMemo(() => {
    const productWarehouseDetailsMap: { [productId: number]: IProductWarehouseDetail } = {};
    data?.listData?.edges?.forEach((productWarehouseDetail) => {
      productWarehouseDetailsMap[productWarehouseDetail?.node?.product!.id!] =
        productWarehouseDetail?.node as IProductWarehouseDetail;
    });

    return productWarehouseDetailsMap;
  }, [data?.listData?.edges]);
};

export const ComponentGrid = memo(
  ({ components, field, rowIndex, performTemporarySave, readOnly }: IComponentGridProps) => {
    const componentColumnMeta = useComponentColumnMeta(rowIndex);
    const connectedForm = useContext(ConnectedFormContext);
    const { createSpecialOrderProduct } = useContext(SalesOrderContext);
    const dispatch = useDispatch();
    const [addCommentToRowIdx, setAddCommentToRowIdx] = useState<Maybe<number>>(null);

    //We only display this grid if the product class is kit, so there 'must' be a matching salesOrderLine
    const salesOrderLine = (connectedForm.values as ISalesOrder).orderLines![rowIndex]!;
    const salesOrderLineOutstanding = useOutstandingQuantity({ salesOrderLine });

    const flattenedComponents: Array<IFlattenedProductComponent> = useMemo(
      () =>
        components
          ? components.flatMap((component, index) => {
              return flatten(component!, [index + 1], !!component!.deleted);
            })
          : [],
      [components]
    );

    //Get the stock totals for the selected line + all the components (For display of correct free stock figure & status highlight in grid).
    const productWarehouseDetailsMap = useProductWarehouseDetails({ salesOrderLine, flattenedComponents });

    const flattenedComponentsWithTotals = useMemo<Array<IFlattenedProductComponent>>(
      () =>
        flattenedComponents.map((flattenedComponent) => {
          const total =
            productWarehouseDetailsMap[flattenedComponent.component!.id!] ?? ({} as IProductWarehouseDetail);

          return { ...flattenedComponent, productWarehouseDetail: total };
        }),
      [flattenedComponents, productWarehouseDetailsMap]
    );

    const contextActionButtons = useMemo(() => {
      const onAddLineCommentClick: IContextMenuItemProps['onClick'] = (value, close, rowIdx) => {
        close?.();
        setAddCommentToRowIdx(rowIdx);
      };

      return ({ rowData, rowIdx }: { rowData: IFlattenedProductComponent; rowIdx: number }) => {
        //Could use context to pass the 'onClick(s)' through, then this could be a component rather than defined in here.
        const commentButton =
          !rowData.comment && rowData.index?.length < 2 ? (
            <MenuItem
              key={`actionButtonComment_${rowIdx}`}
              onClick={(e) => {
                e.preventDefault();
                onAddLineCommentClick(null, undefined, rowIdx);
              }}
            >
              Add Comment
            </MenuItem>
          ) : null;
        return commentButton ? [commentButton] : ([] as Array<JSX.Element>);
      };
    }, [setAddCommentToRowIdx]);

    const getDataIdPrefix: CommentRowContextType['getDataId'] = (rowProps) => {
      const flatRow = flattenedComponentsWithTotals[rowProps.idx];
      if (!flatRow) {
        return null;
      }

      let dataIdPrefix = `${field}.${rowIndex}`;
      for (let i = 0; i < flatRow.index.length; i++) {
        dataIdPrefix += `.components.${flatRow.index[i] - 1}`;
        if (i < flatRow.index.length - 1) {
          dataIdPrefix += '.component';
        }
      }

      return dataIdPrefix;
    };

    const allowCommentsForRow: CommentRowContextType['allowCommentsForRow'] = (rowProps) => {
      return !(flattenedComponentsWithTotals[rowProps.idx]?.index?.length > 1);
    };

    return (
      <ComponentContext.Provider
        value={{
          flattenedComponents: flattenedComponentsWithTotals,
          field,
          rowIndex,
          addCommentToRowIdx,
          setAddCommentToRowIdx,
          lineOutstandingLessFreeStock: Math.max(
            salesOrderLineOutstanding - (productWarehouseDetailsMap[salesOrderLine.id!]?.freeStock ?? 0),
            0
          ),
        }}
      >
        <GridContainer>
          <CommentRowContext.Provider
            value={{
              selectRow: setAddCommentToRowIdx,
              addCommentToRowIdx: addCommentToRowIdx,
              getDataId: getDataIdPrefix,
              allowCommentsForRow: allowCommentsForRow,
              enableSnippets: false,
            }}
          >
            <EditableDataGrid
              className={'ignore-react-onclickoutside'}
              columns={componentColumnMeta}
              listData={flattenedComponentsWithTotals}
              rowsCount={(flattenedComponentsWithTotals?.length || 0) + 1}
              addBlankRow={true}
              enableCellSelect={true}
              enableCellAutoFocus={false}
              readOnly={readOnly}
              onRowUpdated={(commit) => {
                if (!commit.updated) return;

                const index = commit.original?.index[0] ? commit.original.index[0] - 1 : components?.length;

                const rowKey = `${field}.${rowIndex}.components.${index}`;

                const componentRowUuid = getIn(connectedForm.values, `${rowKey}.uuid`);

                // if selected product is a special order template, generate a new product and display the (mini) product form.
                if (
                  !commit.updated.deleted &&
                  commit.updated.component?.template &&
                  commit.updated.component.specialOrder
                ) {
                  createSpecialOrderProduct?.(commit.updated.component.id, commit.rowIdx, {
                    dataid: `${field}.${rowIndex}.components.${index}`,
                    fieldNameOverride: 'component',
                  });
                  return;
                }

                const key = `${rowKey}.${commit.cellKey as string}`;

                if (!componentRowUuid) {
                  dispatch(connectedForm.change(`${rowKey}.uuid`, uuIdGenerator()));
                }
                dispatch(connectedForm.change(key, commit.updated[commit.cellKey as string]));
                performTemporarySave();
              }}
              rowRenderer={LazyCommentRowRenderer}
              enableContextMenu={true}
              renderActionButtons={contextActionButtons}
              disableVirtualRows={true}
              // alwaysSetInactiveOnCommit={true}
            />
          </CommentRowContext.Provider>
        </GridContainer>
      </ComponentContext.Provider>
    );
  },
  (prevProps, nextProps) => {
    return JSON.stringify(prevProps.components) === JSON.stringify(nextProps.components);
  }
);
